import { Circle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { formatRelativeTime } from '@/lib/utils'
import { Tooltip } from '@/components/ui/tooltip'

/**
 * Live online/offline indicator (Change #027).
 *
 * Tri-state by design:
 * - true  → green pill, "Online"
 * - false → muted pill, "Offline"
 * - null  → renders nothing (FTP cameras have no heartbeat protocol;
 *           the IngestionTypeBadge already conveys "FTP" so absence here
 *           is unambiguous, not a UI bug).
 *
 * Matches the same pill pattern as IngestionTypeBadge / Shared.
 */
interface OnlineStatusBadgeProps {
  isOnline: boolean | null
  lastSeen: string | null
  className?: string
  /**
   * When true, use high-contrast translucent styling readable on any photo
   * background (for thumbnail overlays).
   */
  overlay?: boolean
}

export function OnlineStatusBadge({ isOnline, lastSeen, className, overlay = false }: OnlineStatusBadgeProps) {
  const { t } = useTranslation('cameras')

  if (isOnline === null) {
    return null
  }

  const tooltip = lastSeen
    ? t('status.lastSeenTooltip', { time: formatRelativeTime(lastSeen) })
    : t('status.neverSeenTooltip')

  let toneClasses: string
  if (overlay) {
    toneClasses = isOnline
      ? 'bg-green-500/80 text-white border-green-300/40 backdrop-blur-sm shadow-sm'
      : 'bg-black/50 text-white border-white/20 backdrop-blur-sm shadow-sm'
  } else {
    toneClasses = isOnline
      ? 'bg-green-50 text-green-700 border-green-200 dark:bg-green-950/30 dark:text-green-400 dark:border-green-900'
      : 'bg-muted/50 text-muted-foreground border-border'
  }

  return (
    <Tooltip content={tooltip}>
      <span
        className={cn(
          'inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium border shrink-0',
          toneClasses,
          className,
        )}
      >
        <Circle className={cn('h-2 w-2', isOnline ? 'fill-current' : '')} />
        {isOnline ? t('status.online') : t('status.offline')}
      </span>
    </Tooltip>
  )
}
